import { useState } from "react";

interface ProfileTabProps {
  onShowBandDetail: (bandName: string) => void;
  onShowBandMembers: (bandName: string) => void;
  onShowRecruitmentDetail: (recruitmentTitle: string) => void;
}

type ProfileSection =
  | "my-bands"
  | "my-recruitments"
  | "my-applications"
  | "experience"
  | "basic-info";

const ProfileTab = ({
  onShowBandDetail,
  onShowBandMembers,
  onShowRecruitmentDetail,
}: ProfileTabProps) => {
  const [activeSection, setActiveSection] =
    useState<ProfileSection>("my-bands");

  return (
    <div>
      {/* Profile Summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
        <div className="text-center mb-6">
          <div className="w-24 h-24 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
            YT
          </div>
          <h2 className="text-xl font-semibold">山田太郎</h2>
          <p className="text-gray-600">ドラマー • 東京都</p>
        </div>

        <button className="w-full gradient-bg text-white py-3 rounded-lg font-medium hover:opacity-90 transition-opacity">
          プロフィールを編集
        </button>
      </div>

      {/* Profile Menu */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <button
          onClick={() => setActiveSection("my-bands")}
          className="bg-white p-4 rounded-xl shadow-sm card-hover text-center"
        >
          <div className="text-2xl mb-2">🎸</div>
          <div className="font-semibold text-sm">自分のバンド</div>
        </button>
        <button
          onClick={() => setActiveSection("my-recruitments")}
          className="bg-white p-4 rounded-xl shadow-sm card-hover text-center"
        >
          <div className="text-2xl mb-2">📢</div>
          <div className="font-semibold text-sm">自分の募集</div>
        </button>
        <button
          onClick={() => setActiveSection("my-applications")}
          className="bg-white p-4 rounded-xl shadow-sm card-hover text-center"
        >
          <div className="text-2xl mb-2">📝</div>
          <div className="font-semibold text-sm">自分の応募</div>
        </button>
        <button
          onClick={() => setActiveSection("experience")}
          className="bg-white p-4 rounded-xl shadow-sm card-hover text-center"
        >
          <div className="text-2xl mb-2">🎵</div>
          <div className="font-semibold text-sm">演奏歴</div>
        </button>
        <button
          onClick={() => setActiveSection("basic-info")}
          className="bg-white p-4 rounded-xl shadow-sm card-hover text-center"
        >
          <div className="text-2xl mb-2">👤</div>
          <div className="font-semibold text-sm">基本情報</div>
        </button>
      </div>

      {/* My Bands Section */}
      {activeSection === "my-bands" && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold mb-4 flex items-center">
            <span className="mr-2">🎸</span>
            自分のバンド (2)
          </h3>
          <div className="space-y-4">
            <div
              className="border border-gray-200 rounded-lg p-4 card-hover cursor-pointer"
              onClick={() => onShowBandDetail("The Rockers")}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    TR
                  </div>
                  <div>
                    <h4 className="font-semibold">The Rockers</h4>
                    <p className="text-sm text-gray-600">ロック • 5人</p>
                  </div>
                </div>
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  リーダー
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                週末中心の活動で、ライブハウスでの演奏を目指しています。
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="mr-3">📅 週2回</span>
                  <span>🎯 次回ライブ: 3/15</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowBandMembers("The Rockers");
                  }}
                  className="text-blue-500 text-sm hover:underline"
                >
                  メンバー管理
                </button>
              </div>
            </div>

            <div
              className="border border-gray-200 rounded-lg p-4 card-hover cursor-pointer"
              onClick={() => onShowBandDetail("Jazz Session")}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    JS
                  </div>
                  <div>
                    <h4 className="font-semibold">Jazz Session</h4>
                    <p className="text-sm text-gray-600">ジャズ • 4人</p>
                  </div>
                </div>
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  メンバー
                </span>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                月2回のセッションを中心とした活動です。
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="mr-3">📅 月2回</span>
                  <span>🎯 次回セッション: 3/10</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowBandMembers("Jazz Session");
                  }}
                  className="text-blue-500 text-sm hover:underline"
                >
                  メンバー確認
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My Recruitments Section */}
      {activeSection === "my-recruitments" && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold mb-4 flex items-center">
            <span className="mr-2">📢</span>
            自分の募集 (3)
          </h3>
          <div className="space-y-4">
            <div
              className="border border-gray-200 rounded-lg p-4 card-hover cursor-pointer"
              onClick={() => onShowRecruitmentDetail("ドラム募集")}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold">ドラム募集 - The Rockers</h4>
                  <p className="text-sm text-gray-600">投稿日: 2024/02/20</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    募集中
                  </span>
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    応募5件
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                週末中心の活動で、ライブハウスでの演奏を目指しています。初心者歓迎！
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="mr-3">📅 週1-2回</span>
                  <span>⭐ 初心者OK</span>
                </div>
                <div className="flex space-x-2">
                  <button className="text-blue-500 text-sm hover:underline">
                    編集
                  </button>
                  <button className="text-red-500 text-sm hover:underline">
                    削除
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My Applications Section */}
      {activeSection === "my-applications" && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold mb-4 flex items-center">
            <span className="mr-2">📝</span>
            自分の応募 (3)
          </h3>
          <div className="space-y-3">
            <div className="bg-white rounded-xl p-4 border border-gray-200 card-hover">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    TR
                  </div>
                  <div>
                    <h4 className="font-semibold">The Rockers</h4>
                    <p className="text-sm text-gray-600">ドラム募集</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                    審査中
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                週末中心の活動で、ライブハウスでの演奏を目指しています。
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="mr-3">📅 応募日: 2024/02/25</span>
                  <span>📍 東京都</span>
                </div>
                <div className="flex space-x-2">
                  <button className="text-blue-500 text-sm hover:underline">
                    詳細
                  </button>
                  <button className="text-red-500 text-sm hover:underline">
                    取消
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-gray-200 card-hover">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    JC
                  </div>
                  <div>
                    <h4 className="font-semibold">Jazz Collective</h4>
                    <p className="text-sm text-gray-600">ベース募集</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                    承認済み
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                本格的なジャズセッションを楽しみたい方募集。
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="mr-3">📅 応募日: 2024/02/20</span>
                  <span>📍 大阪府</span>
                </div>
                <div className="flex space-x-2">
                  <button className="text-blue-500 text-sm hover:underline">
                    詳細
                  </button>
                  <button className="text-green-500 text-sm hover:underline">
                    参加
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-gray-200 card-hover">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    FM
                  </div>
                  <div>
                    <h4 className="font-semibold">Funk Masters</h4>
                    <p className="text-sm text-gray-600">ドラム募集</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                    不採用
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                ファンク系のグルーヴを重視したバンドです。
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  <span className="mr-3">📅 応募日: 2024/02/15</span>
                  <span>📍 神奈川県</span>
                </div>
                <div className="flex space-x-2">
                  <button className="text-blue-500 text-sm hover:underline">
                    詳細
                  </button>
                  <button className="text-gray-400 text-sm cursor-not-allowed">
                    完了
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Experience Section */}
      {activeSection === "experience" && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold mb-4 flex items-center">
            <span className="mr-2">🎵</span>
            演奏歴・スキル
          </h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold mb-3">楽器別経験年数</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🥁</span>
                    <div>
                      <div className="font-medium">ドラム</div>
                      <div className="text-sm text-gray-600">メイン楽器</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-blue-600">10年</div>
                    <div className="text-xs text-gray-500">上級者</div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🎵</span>
                    <div>
                      <div className="font-medium">パーカッション</div>
                      <div className="text-sm text-gray-600">サブ楽器</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-green-600">5年</div>
                    <div className="text-xs text-gray-500">中級者</div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🎹</span>
                    <div>
                      <div className="font-medium">キーボード</div>
                      <div className="text-sm text-gray-600">趣味程度</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-yellow-600">2年</div>
                    <div className="text-xs text-gray-500">初級者</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3">バンド歴</h4>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="font-medium">The Rockers</div>
                  <div className="text-sm text-gray-600">
                    2022年〜現在 (リーダー・ドラム)
                  </div>
                  <div className="text-sm text-gray-700 mt-1">
                    ライブハウス公演5回、オリジナル楽曲3曲
                  </div>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="font-medium">Jazz Session</div>
                  <div className="text-sm text-gray-600">
                    2023年〜現在 (ドラム)
                  </div>
                  <div className="text-sm text-gray-700 mt-1">
                    月例セッション参加、ジャズフェス出演1回
                  </div>
                </div>
                <div className="border-l-4 border-gray-400 pl-4">
                  <div className="font-medium">Blue Moon</div>
                  <div className="text-sm text-gray-600">
                    2020年〜2022年 (ドラム)
                  </div>
                  <div className="text-sm text-gray-700 mt-1">
                    ブルース中心、ライブハウス公演8回
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3">主な実績</h4>
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <span className="text-yellow-600 mr-2">🏆</span>
                    <div>
                      <div className="font-medium">渋谷ロックフェス2023</div>
                      <div className="text-sm text-gray-600">
                        The Rockersで出演
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <span className="text-blue-600 mr-2">🎵</span>
                    <div>
                      <div className="font-medium">オリジナル楽曲制作</div>
                      <div className="text-sm text-gray-600">
                        作詞・作曲・編曲経験あり
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">🎤</span>
                    <div>
                      <div className="font-medium">レコーディング経験</div>
                      <div className="text-sm text-gray-600">
                        スタジオ録音・ミックス経験
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Basic Info Section */}
      {activeSection === "basic-info" && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold mb-4 flex items-center">
            <span className="mr-2">👤</span>
            基本情報
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">🎵 演奏楽器</h4>
              <div className="flex flex-wrap gap-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  ドラム
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                  パーカッション
                </span>
                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                  キーボード
                </span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🎸 好きなジャンル</h4>
              <div className="flex flex-wrap gap-2">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                  ロック
                </span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                  ポップス
                </span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
                  メタル
                </span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">
                  ジャズ
                </span>
                <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm">
                  ブルース
                </span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">📅 希望活動頻度</h4>
              <p className="text-gray-700">週1-2回</p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">📍 活動可能エリア</h4>
              <p className="text-gray-700">東京都、神奈川県</p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🎯 音楽活動の目標</h4>
              <div className="flex flex-wrap gap-2">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">
                  ライブ活動
                </span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">
                  オリジナル制作
                </span>
                <span className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm">
                  スキルアップ
                </span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">💬 自己紹介</h4>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                ドラム歴10年です。ロックを中心に様々なジャンルを演奏してきました。現在2つのバンドで活動中で、ライブ経験も豊富です。新しいバンドでの活動も楽しみにしています！音楽を通じて多くの人とつながりたいと思っています。
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileTab;
